import { BaseQueryFn, EndpointBuilder } from '@reduxjs/toolkit/query';
import { BaseQueryParams } from 'types/base-query-params';
import { PaginatedResponse } from 'types/dtos';
import { ResponseCompanyOwnerDto } from '../types/dtos';
import { constructQueryParams } from 'utils/query-params-builder';
import { APP_ENDPOINTS } from 'app/endpoints';
import { checkError, fetchResponse } from 'utils/api-helpers';
import { handleDuplicates, removeUndefined } from 'utils/array-helpers';
import { ResponseAccountDto } from 'features/account/types/dtos';
import { formatDate } from 'utils/date-helpers';
import { ResponseCompanyDto } from 'features/company/types/dtos';

export type CompanyOwnersWithJoinQueryParams = BaseQueryParams & {
    accountId?: string;
};

export default (builder: EndpointBuilder<BaseQueryFn, string, string>) =>
    builder.query<PaginatedResponse<ResponseCompanyOwnerDto>, CompanyOwnersWithJoinQueryParams>({
        async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
            const companyOwnersResponse = await fetchWithBQ(constructQueryParams(APP_ENDPOINTS.COMPANY_OWNERS, _arg));
            const companyOwnersError = checkError(companyOwnersResponse);
            if (companyOwnersError) return companyOwnersError;
            const companyOwners = companyOwnersResponse.data as PaginatedResponse<ResponseCompanyOwnerDto>;

            const accountIds = removeUndefined(
                handleDuplicates(companyOwners.content.map((companyOwner) => companyOwner.accountId)),
            );
            const accountsResponse = await fetchResponse(APP_ENDPOINTS.ACCOUNTS, accountIds, fetchWithBQ);
            const accountsError = checkError(accountsResponse);
            if (accountsError) return accountsError;
            const accounts = accountsResponse.data as PaginatedResponse<ResponseAccountDto>;

            const companyIds = removeUndefined(
                handleDuplicates(companyOwners.content.map((companyOwner) => companyOwner.companyId)),
            );
            const companiesResponse = await fetchResponse(APP_ENDPOINTS.COMPANIES, companyIds, fetchWithBQ);
            const companiesError = checkError(companiesResponse);
            if (companiesError) return companiesError;
            const companies = companiesResponse.data as PaginatedResponse<ResponseCompanyDto>;

            companyOwners.content = companyOwners.content.map((companyOwner) => {
                const email =
                    accounts.content.find((account) => {
                        return account.id === companyOwner.accountId;
                    })?.email || '';

                const companyName = companies.content.find((company) => {
                    return company.id === companyOwner.companyId;
                })?.name;

                const createdAt = formatDate(companyOwner.createdAt);

                return {
                    ...companyOwner,
                    email,
                    companyName,
                    createdAt,
                };
            });

            return {
                data: companyOwners,
            };
        },
        providesTags: [{ type: 'company-owners-with-join', id: 'GET-JOIN' }],
    });
